import { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { CatalogHotspot } from './CatalogHotspot';
import { PinchZoom, Swiper } from 'dibs-pinch-zoom-swipe';
import { Spinner } from 'dibs-elements/exports/Spinner';
import * as tracking from 'dibs-tracking';
import serverVars from 'server-vars';
import get from 'lodash.get';
import {
    setCurrentPage as setCurrentPageDispatch,
    setErrorOccurred as setErrorOccurredDispatch,
    setSinglePageDimensions as setSinglePageDimensionsDispatch,
    trackZoomDrag as trackZoomDragDispatch,
    turnOffDrag as turnOffDragDispatch,
    turnOnDrag as turnOnDragDispatch,
} from '../actionCreators/uiActionCreators';
import {
    calculatedPageNumberText,
    calculateTotalPages,
    getCanvasDimensions,
    getScaledPageDimensions,
    isMobilePortrait,
    isSinglePage,
} from '../catalogHelpers';

import styles from '../styles/catalogPage.scss';
import { DESKTOP, TABLET, MOBILE } from '../../utils/deviceType';

const CDN_HOST = serverVars.get('cdnDomain');

const initialZoomState = {
    startingX: null,
    startingY: null,
    currentOffsetX: null,
    currentOffsetY: null,
};

export class CatalogPageComponent extends Component {
    constructor(props) {
        super(props);

        this.state = { ...initialZoomState };

        this.startZoomMove = this.startZoomMove.bind(this);
        this.trackZoomMove = this.trackZoomMove.bind(this);
        this.stopZoomMove = this.stopZoomMove.bind(this);
        this.buildLinkLayer = this.buildLinkLayer.bind(this);
        this.buildSwipeElements = this.buildSwipeElements.bind(this);
        this.buildSwipePage = this.buildSwipePage.bind(this);
        this.performSwipe = this.performSwipe.bind(this);
    }

    startZoomMove(e) {
        const { turnOnDrag, zoomedIn } = this.props;
        if (zoomedIn) {
            this.setState({
                startingX: e.clientX,
                startingY: e.clientY,
                currentOffsetX: null,
                currentOffsetY: null,
            });
            turnOnDrag();
        }
    }

    stopZoomMove() {
        const { cummulativeDragX, cummulativeDragY, isDragging, trackZoomDrag, turnOffDrag } =
            this.props;

        // reset defaults
        if (isDragging) {
            this.setState({ ...initialZoomState });

            // combine new drag offset with existing offset
            trackZoomDrag(
                cummulativeDragX + this.state.currentOffsetX,
                cummulativeDragY + this.state.currentOffsetY
            );
            turnOffDrag();
        }
    }

    trackZoomMove(e) {
        const {
            catalogPage,
            cummulativeDragX,
            cummulativeDragY,
            currentPage,
            deviceType,
            isDragging,
            margin,
            scale,
            screenOrientation,
            singlePageRatio,
            zoomedIn,
            zoomedScale,
        } = this.props;

        const { pageCount, singlePageDimensions } = get(catalogPage, 'images') || {};

        if (isDragging) {
            const originalScale = scale;
            const currentScale = zoomedIn ? zoomedScale : originalScale;

            // calculate max we can drag before we start to pull the page out of the canvas
            const scaledPageDimensions = getScaledPageDimensions(
                currentPage,
                pageCount,
                singlePageDimensions,
                currentScale
            );
            const canvasDimensions = getCanvasDimensions(
                singlePageDimensions,
                scale,
                margin,
                deviceType,
                screenOrientation,
                singlePageRatio
            );
            const maxDragX = scaledPageDimensions.width - canvasDimensions.width;
            const maxDragY = scaledPageDimensions.height - canvasDimensions.height;

            // calculate how much we have dragged since mouse was last up
            const newOffsetX = this.state.startingX - e.clientX;
            const newOffsetY = this.state.startingY - e.clientY;

            // calculate total drag since in zoom mode
            const sumOffsetX = cummulativeDragX + newOffsetX;
            const sumOffsetY = cummulativeDragY + newOffsetY;

            // fall back if user drags too far
            // if dragged too far right make margin 0 (starting point)
            // if dragged too far left make margin max possible
            let fallBackX = e.clientX > this.state.startingX ? 0 : maxDragX;

            // if dragged too far down make margin 0 (starting point)
            // if dragged too far up make margin max possible
            let fallBackY = e.clientY > this.state.startingY ? 0 : maxDragY;

            // take cummulative drag into account for fallbacks since this will be combined with current offset
            fallBackX -= cummulativeDragX;
            fallBackY -= cummulativeDragY;

            // use new offset or fallback is dragged too far
            this.setState({
                currentOffsetX: sumOffsetX < maxDragX && sumOffsetX > 0 ? newOffsetX : fallBackX,
                currentOffsetY: sumOffsetY < maxDragY && sumOffsetY > 0 ? newOffsetY : fallBackY,
            });
        }
    }

    buildLinkLayer() {
        const {
            catalogPage,
            currentPage,
            cummulativeDragX,
            cummulativeDragY,
            deviceType,
            isPageRight,
            mobileScale,
            quickViewHotspot,
            scale,
            screenOrientation,
            showQuickViewSpinner,
            singlePageRatio,
            tabletScale,
            zoomedIn,
            zoomedScale,
        } = this.props;

        const catalogHotspots = get(catalogPage, 'hotspots') || [];
        const { pageCount, singlePageDimensions } = get(catalogPage, 'images') || {};
        const catalogHotspotsArr = Array.isArray(catalogHotspots) ? catalogHotspots : [];
        const isSingle = isSinglePage(currentPage, pageCount);
        const isDesktop = deviceType === DESKTOP;
        const isPhonePortrait = isMobilePortrait(deviceType, screenOrientation);
        const linkLayerStyles = {};

        let dynamicScale; // for mobile portrait which is stretched to width

        if (isPhonePortrait) {
            const scaledWidth = window.innerWidth;
            linkLayerStyles.width = scaledWidth;

            linkLayerStyles.height = Math.ceil(scaledWidth / singlePageRatio);

            if (!isSingle) {
                linkLayerStyles.height = linkLayerStyles.height * 2;
            }

            if (singlePageDimensions && singlePageDimensions.width) {
                dynamicScale = scaledWidth / singlePageDimensions.width;
            }
        } else {
            let currentScale = zoomedIn ? zoomedScale : scale;
            if (deviceType === TABLET) {
                currentScale = tabletScale;
            } else if (deviceType === MOBILE) {
                currentScale = mobileScale;
            }
            const scaledPageDimensions = getScaledPageDimensions(
                currentPage,
                pageCount,
                singlePageDimensions,
                currentScale
            );

            // size layer with hotspot links to match if currently zoomed in or out
            linkLayerStyles.height = Math.ceil(scaledPageDimensions.height) + 'px';
            linkLayerStyles.width = Math.ceil(scaledPageDimensions.width) + 'px';
        }

        // combine current drag with any existing drag in zoom mode
        const marginLeft = zoomedIn ? Math.max(cummulativeDragX + this.state.currentOffsetX, 0) : 0;
        const marginTop = zoomedIn ? Math.max(cummulativeDragY + this.state.currentOffsetY, 0) : 0;

        // move layer with hotspot links as pdf page gets dragged around
        if (zoomedIn) {
            linkLayerStyles.top = -marginTop + 'px';
            linkLayerStyles.left = isSingle ? '50%' : -marginLeft + 'px';
            linkLayerStyles.transform = isSingle ? 'translate(-50%, 0) rotate(0deg)' : 'none';
        }

        // for non-icons and/or non-desktop devices, show spinner on page
        const renderSpinner =
            showQuickViewSpinner &&
            catalogHotspotsArr.find(_hotspot => {
                const hotspotIsABox = _hotspot.formType === 'box';
                const qvKey = quickViewHotspot && quickViewHotspot.hotspotKey;
                if ((hotspotIsABox || !isDesktop) && qvKey) {
                    return _hotspot.hotspotKey === qvKey;
                }
                return false;
            });

        return (
            <div
                className={classNames({
                    [styles.pageLinkLayerDesktop]: isDesktop,
                    [styles.pageLinkLayerNonDesktop]: !isDesktop,
                })}
                style={linkLayerStyles}
            >
                {catalogHotspotsArr.reduce((hotspotsArr, hotspot) => {
                    if (hotspot.page === currentPage) {
                        return hotspotsArr.concat(
                            <CatalogHotspot
                                catalogPage={catalogPage}
                                deviceType={deviceType}
                                dynamicScale={dynamicScale}
                                hotspot={hotspot}
                                key={hotspot.hotspotId}
                            />
                        );
                    }
                    return hotspotsArr;
                }, [])}
                {renderSpinner && (
                    <Spinner
                        containerClass={classNames({
                            [styles.quickViewLoaderCenter]: true,
                            [styles.leftPageLoader]: isPhonePortrait && !isSingle && !isPageRight,
                            [styles.rightPageLoader]: isPhonePortrait && !isSingle && isPageRight,
                        })}
                    />
                )}
            </div>
        );
    }

    buildSwipePage(currentScale, pageNum, isPhonePortrait, isCurrSingle, displayRightSide) {
        const { catalogPage, deviceType } = this.props;

        const { prefix, singlePageDimensions } = get(catalogPage, 'images') || {};

        let scaledWidth;
        if (isPhonePortrait) {
            scaledWidth = window.innerWidth * (!isCurrSingle ? 2 : 1);
        } else {
            scaledWidth = singlePageDimensions.width * currentScale;

            if (!isCurrSingle) {
                scaledWidth *= 2;
            }
        }

        const styleObj = {};

        if (isCurrSingle || !isPhonePortrait) {
            styleObj.width = `${scaledWidth}px`;
        }

        return (
            <img
                className={classNames(styles.pdfPage, {
                    [styles.pdfPageNonDesktop]: deviceType !== DESKTOP,
                    [styles.pdfPageMobilePortraitDouble]: isPhonePortrait && !isCurrSingle,
                    [styles.rightPage]: isPhonePortrait && displayRightSide,
                })}
                style={styleObj}
                src={`${CDN_HOST}/pdf-editor/${prefix}_${pageNum}.jpg`}
            />
        );
    }

    buildSwipeElements() {
        const {
            catalogPage,
            currentPage,
            deviceType,
            mobileScale,
            scale,
            screenOrientation,
            tabletScale,
            zoomedIn,
            zoomedScale,
        } = this.props;

        const pageCount = get(catalogPage, 'images.pageCount');

        let currentScale = zoomedIn ? zoomedScale : scale;
        if (deviceType === TABLET) {
            currentScale = tabletScale;
        } else if (deviceType === MOBILE) {
            currentScale = mobileScale;
        }

        const isPhonePortrait = isMobilePortrait(deviceType, screenOrientation);

        const pagesArr = [];

        for (let j = 1; j <= pageCount; j++) {
            const isCurrSingle = isSinglePage(j, pageCount);

            pagesArr.push(
                <div datatn={`swipe-list-item-${j}`} key={`swipe-list-item-${j}`}>
                    {this.buildSwipePage(currentScale, j, isPhonePortrait, isCurrSingle, false)}
                    {j === currentPage && this.buildLinkLayer()}
                </div>
            );

            // add right side as next page
            if (!isCurrSingle && isPhonePortrait) {
                pagesArr.push(
                    <div datatn={`swipe-list-item-${j}-right`} key={`swipe-list-item-${j}-right`}>
                        {this.buildSwipePage(currentScale, j, isPhonePortrait, isCurrSingle, true)}
                    </div>
                );
            }
        }

        return pagesArr;
    }

    performSwipe(index, previousIndex) {
        const { catalogPage, deviceType, setCurrentPage } = this.props;
        const pageCount = get(catalogPage, 'images.pageCount');

        const pdfPageNum = index + 1;
        const direction = index < previousIndex ? 'previous' : 'next';
        let pageNumText = calculatedPageNumberText(
            pdfPageNum,
            pageCount,
            false // count spread pages as one unit
        );

        // count spread pages split up
        // need to look at screen width and height
        // screenOrientation may have changed since function has
        // been bound to the swiper
        if (window.innerWidth < window.innerHeight && deviceType === MOBILE) {
            pageNumText = `Page ${pdfPageNum} / ${calculateTotalPages(pageCount)}`;

            // calculate page number in pdf file
            const pdfPageId = Math.ceil(index / 2) + 1;
            let pageLeft = false;
            let pageRight = false;

            if (isSinglePage(pdfPageId, pageCount)) {
                setCurrentPage(pdfPageId);
            } else {
                const isPageEven = (index + 1) % 2 === 0;
                if (isPageEven) {
                    pageLeft = true;
                } else {
                    pageRight = true;
                }
                setCurrentPage(pdfPageId, pageLeft, pageRight);
            }
        } else {
            setCurrentPage(index + 1);
        }

        tracking.trackEvent({
            category: 'product catalog',
            action: `pagination - ${direction} button`,
            label: pageNumText,
        });
    }

    render() {
        const {
            catalogPage,
            currentPage,
            cummulativeDragX,
            cummulativeDragY,
            deviceType,
            isPageRight,
            scale,
            screenOrientation,
            tabletScale,
            zoomedIn,
            zoomedScale,
        } = this.props;

        const { pageCount, prefix, singlePageDimensions } = get(catalogPage, 'images') || {};

        if (!currentPage) {
            return null;
        }

        let currentScale = zoomedIn ? zoomedScale : scale;
        if (deviceType === TABLET) {
            currentScale = tabletScale;
        }

        const isSingle = isSinglePage(currentPage, pageCount);
        const isPhonePortrait = isMobilePortrait(deviceType, screenOrientation);
        const totalPageCount = calculateTotalPages(pageCount);

        // combine current drag with any existing drag in zoom mode
        const marginLeft = zoomedIn ? Math.max(cummulativeDragX + this.state.currentOffsetX, 0) : 0;
        const marginTop = zoomedIn ? Math.max(cummulativeDragY + this.state.currentOffsetY, 0) : 0;

        let calculatedWidth = singlePageDimensions.width * currentScale;

        if (!isSingle) {
            calculatedWidth *= 2;
        }

        if (deviceType === DESKTOP) {
            return (
                <div
                    style={{
                        marginLeft: -marginLeft + 'px',
                        marginTop: -marginTop + 'px',
                    }}
                    onMouseDown={this.startZoomMove}
                    onMouseMove={this.trackZoomMove}
                    onMouseUp={this.stopZoomMove}
                    onMouseOut={this.stopZoomMove}
                >
                    <img
                        className={styles.pdfPage}
                        src={`${CDN_HOST}/pdf-editor/${prefix}_${currentPage}.jpg?width=${Math.ceil(
                            calculatedWidth
                        )}`}
                    />
                    {this.buildLinkLayer()}
                </div>
            );
        } else {
            // calculate which swipe index we should be at
            let swipePage = currentPage;

            if (isPhonePortrait) {
                if (currentPage === 1) {
                    swipePage = 1;
                } else if (currentPage === pageCount) {
                    swipePage = totalPageCount;
                } else {
                    // assume left side first
                    const doublePage = currentPage * 2;
                    swipePage = doublePage - 2;

                    if (isPageRight) {
                        swipePage += 1;
                    }
                }
            }

            return (
                <PinchZoom enabled overridePanMoveDefault>
                    <div>
                        <Swiper
                            swipeDisabled={false}
                            onPageChange={this.performSwipe}
                            startingPage={swipePage - 1}
                        >
                            {this.buildSwipeElements()}
                        </Swiper>
                    </div>
                </PinchZoom>
            );
        }
    }
}

CatalogPageComponent.propTypes = {
    catalogPage: PropTypes.object,
    currentPage: PropTypes.number,
    cummulativeDragX: PropTypes.number,
    cummulativeDragY: PropTypes.number,
    deviceType: PropTypes.string,
    isDragging: PropTypes.bool,
    isPageRight: PropTypes.bool,
    margin: PropTypes.number,
    mobileScale: PropTypes.number,
    quickViewHotspot: PropTypes.object,
    scale: PropTypes.number,
    screenOrientation: PropTypes.string,
    setCurrentPage: PropTypes.func,
    setErrorOccurred: PropTypes.func,
    setSinglePageDimensions: PropTypes.func,
    showQuickViewSpinner: PropTypes.bool,
    singlePageRatio: PropTypes.number,
    tabletScale: PropTypes.number,
    trackZoomDrag: PropTypes.func,
    turnOffDrag: PropTypes.func,
    turnOnDrag: PropTypes.func,
    zoomedIn: PropTypes.bool,
    zoomedScale: PropTypes.number,
};

CatalogPageComponent.defaultProps = {
    deviceType: DESKTOP,
};

const mapStateToProps = ({ qv, ui }) => {
    return {
        currentPage: ui.currentPage,
        cummulativeDragX: ui.cummulativeDragX,
        cummulativeDragY: ui.cummulativeDragY,
        isDragging: ui.isDragging,
        isPageRight: ui.isPageRight,
        margin: ui.margin,
        mobileScale: ui.mobileScale,
        quickViewHotspot: qv.quickViewHotspot,
        scale: ui.scale,
        screenOrientation: ui.screenOrientation,
        showQuickViewSpinner: qv.showQuickViewSpinner,
        singlePageRatio: ui.singlePageRatio,
        tabletScale: ui.tabletScale,
        zoomedIn: ui.zoomedIn,
        zoomedScale: ui.zoomedScale,
    };
};

export const mapDispatchToProps = dispatch => {
    return {
        setCurrentPage: (nextPageNum, pageL, pageR) =>
            dispatch(setCurrentPageDispatch(nextPageNum, pageL, pageR)),
        setErrorOccurred: () => dispatch(setErrorOccurredDispatch()),
        setSinglePageDimensions: (pageHeight, pageWidth) =>
            dispatch(setSinglePageDimensionsDispatch(pageHeight, pageWidth)),
        trackZoomDrag: (cummulativeDragX, cummulativeDragY) =>
            dispatch(trackZoomDragDispatch(cummulativeDragX, cummulativeDragY)),
        turnOffDrag: () => dispatch(turnOffDragDispatch()),
        turnOnDrag: () => dispatch(turnOnDragDispatch()),
    };
};

export const CatalogPage = createFragmentContainer(
    connect(mapStateToProps, mapDispatchToProps)(CatalogPageComponent),
    {
        catalogPage: graphql`
            fragment CatalogPage_catalogPage on CatalogPage {
                hotspots {
                    formType
                    hotspotId
                    hotspotKey
                    page
                    ...CatalogHotspot_hotspot
                }
                images {
                    pageCount
                    prefix
                    singlePageDimensions {
                        height
                        width
                    }
                }
                ...CatalogHotspot_catalogPage
            }
        `,
    }
);
